/**
 * Wizard state store (zustand).
 *
 * NOT persisted in localStorage — server is source of truth via per-field
 * autosave. Remount reads draft back from the server bundle. This avoids
 * stale-draft conflicts when the same user opens the wizard on a second
 * device after editing on the first.
 */

import { create } from "zustand";
import { TOTAL_STEPS } from "../config/steps";

export type Direction = "forward" | "backward";

export interface ProcedureDraft {
  id?: string;
  name: string;
  description?: string | null;
  value_cents?: number | null;
  duration_minutes?: number | null;
  format?: "presencial" | "online" | "hibrido" | null;
}

export interface NoteDraft {
  id?: string;
  title: string;
  content: string;
  tags?: string[];
  pinned?: boolean;
}

export type DraftPatch = Partial<{
  // identity
  honorific_prefix: "" | "Dr." | "Dra." | null;
  display_name: string;
  gender: "M" | "F" | null;
  age: number | null;
  photo_url: string | null;
  // professional
  niche: string | null;
  formations: string[];
  registry_type: string | null;
  registry_number: string | null;
  years_active: number | null;
  clients_served_estimate: number | null;
  // future steps
  bio: string | null;
  unique_mechanism: string | null;
  differentiator: string | null;
  pains_solved: string | null;
  transformation_promise: string | null;
  ticket_medio_cents: number | null;
  tone_formality: number | null;
  banned_words: string[];
  anchor_words: string[];
  example_phrases: string[];
  ethical_constraints: string | null;
  main_offer: string | null;
  secondary_offers: string[];
  funnel_sources: string[];
  goals_90d: string | null;
}>;

export interface OnboardingState {
  currentStepIndex: number;
  direction: Direction;
  draft: DraftPatch;
  isSubmittingStep: boolean;
  lastSavedAt: number | null;
  // actions
  goNext: () => void;
  goPrev: () => void;
  goTo: (index: number) => void;
  setDraftField: <K extends keyof DraftPatch>(key: K, value: DraftPatch[K]) => void;
  mergeDraft: (patch: DraftPatch) => void;
  setSubmitting: (v: boolean) => void;
  markSaved: (at?: number) => void;
  reset: () => void;
}

const clamp = (n: number, max: number): number => Math.max(0, Math.min(max - 1, n));

export const useOnboardingStore = create<OnboardingState>((set) => ({
  currentStepIndex: 0,
  direction: "forward",
  draft: {},
  isSubmittingStep: false,
  lastSavedAt: null,

  goNext: () =>
    set((s) => ({
      currentStepIndex: clamp(s.currentStepIndex + 1, TOTAL_STEPS),
      direction: "forward",
    })),

  goPrev: () =>
    set((s) => ({
      currentStepIndex: clamp(s.currentStepIndex - 1, TOTAL_STEPS),
      direction: "backward",
    })),

  goTo: (index) =>
    set((s) => {
      const next = clamp(index, TOTAL_STEPS);
      return {
        currentStepIndex: next,
        direction: next >= s.currentStepIndex ? "forward" : "backward",
      };
    }),

  setDraftField: (key, value) =>
    set((s) => ({ draft: { ...s.draft, [key]: value } })),

  mergeDraft: (patch) =>
    set((s) => ({ draft: { ...s.draft, ...patch } })),

  setSubmitting: (v) => set(() => ({ isSubmittingStep: v })),

  markSaved: (at = Date.now()) => set(() => ({ lastSavedAt: at })),

  reset: () =>
    set(() => ({
      currentStepIndex: 0,
      direction: "forward",
      draft: {},
      isSubmittingStep: false,
      lastSavedAt: null,
    })),
}));
